/**
 * Conversions between stored documents and editable form state.
 *
 * Forms keep every text field as a string (empty = not set). Saving turns
 * empty optional fields back into "absent" so documents stay minimal, and
 * renumbers displayOrder from list position. Nothing here is about layout.
 */
import type { ExhibitionInput, WorkUpdate } from "@yushi-portfolio/api-types";
import type {
  Credit,
  ExhibitionDocument,
  Installation,
  LocalizedText,
  WorkDocument,
  WorkMedia,
} from "@yushi-portfolio/content-schema";

export type TextPair = { ja: string; en: string };

export type MediaForm = {
  key: string;
  assetId: string;
  kind: WorkMedia["kind"];
  featured: boolean;
  alt: TextPair;
  caption: TextPair;
};

export type InstallationForm = {
  key: string;
  id: string;
  exhibitionId: string;
  title: TextPair;
  year: string;
  venueOverride: TextPair;
  description: TextPair;
  dimensions: TextPair;
  media: MediaForm[];
  technicalInternal: string;
};

export type WorkForm = {
  slug: string;
  title: TextPair;
  type: WorkDocument["type"];
  dateStart: string;
  dateEnd: string;
  ongoing: boolean;
  concept: TextPair;
  medium: TextPair;
  dimensions: TextPair;
  durationSeconds: string;
  media: MediaForm[];
  installations: InstallationForm[];
  credits: { key: string; role: TextPair; name: TextPair }[];
  technicalPublic: TextPair;
  technicalInternal: string;
  /** Draft version the form was loaded from (optimistic locking). */
  version: number;
};

export type ExhibitionForm = {
  slug: string;
  title: TextPair;
  type: ExhibitionDocument["type"];
  venue: string;
  city: string;
  country: string;
  startDate: string;
  endDate: string;
  url: string;
};

let counter = 0;
/** Stable React keys for list rows; never stored. */
export const rowKey = () => `row-${++counter}`;

/** Ids for new nested items (installations). Matches IdSchema. */
export const newInstallationId = () => `ins-${crypto.randomUUID()}`;

const pair = (text: LocalizedText | undefined): TextPair => ({ ja: text?.ja ?? "", en: text?.en ?? "" });
const emptyPair = (): TextPair => ({ ja: "", en: "" });

/** Required text: keeps ja (possibly empty), drops an empty en. */
function required(text: TextPair): LocalizedText {
  const en = text.en.trim();
  return en ? { ja: text.ja.trim(), en } : { ja: text.ja.trim() };
}

/** Optional text: absent when both languages are empty. */
function optional(text: TextPair): LocalizedText | undefined {
  if (!text.ja.trim() && !text.en.trim()) return undefined;
  return required(text);
}

function optionalString(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function optionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  // Invalid numbers are passed through as NaN so schema validation reports them.
  return Number(trimmed);
}

/** Drops undefined values so documents serialize without empty keys. */
function compact<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export function emptyMedia(): MediaForm {
  return { key: rowKey(), assetId: "", kind: "artwork", featured: false, alt: emptyPair(), caption: emptyPair() };
}

function mediaToForm(media: WorkMedia[]): MediaForm[] {
  return [...media]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((m) => ({ key: rowKey(), assetId: m.assetId, kind: m.kind, featured: Boolean(m.featured), alt: pair(m.alt), caption: pair(m.caption) }));
}

function mediaFromForm(rows: MediaForm[]): WorkMedia[] {
  return rows.map((m, index) =>
    compact({
      assetId: m.assetId.trim(),
      kind: m.kind,
      featured: m.featured || undefined,
      alt: optional(m.alt),
      caption: optional(m.caption),
      displayOrder: index,
    }),
  );
}

// ---------------------------------------------------------------------------
// Work
// ---------------------------------------------------------------------------

export function emptyInstallation(): InstallationForm {
  return {
    key: rowKey(),
    id: newInstallationId(),
    exhibitionId: "",
    title: emptyPair(),
    year: "",
    venueOverride: emptyPair(),
    description: emptyPair(),
    dimensions: emptyPair(),
    media: [],
    technicalInternal: "",
  };
}

export function emptyWorkForm(): WorkForm {
  return {
    slug: "",
    title: emptyPair(),
    type: "painting",
    dateStart: String(new Date().getFullYear()),
    dateEnd: "",
    ongoing: false,
    concept: emptyPair(),
    medium: emptyPair(),
    dimensions: emptyPair(),
    durationSeconds: "",
    media: [],
    installations: [],
    credits: [],
    technicalPublic: emptyPair(),
    technicalInternal: "",
    version: 0,
  };
}

export function workToForm(work: WorkDocument): WorkForm {
  return {
    slug: work.slug,
    title: pair(work.title),
    type: work.type,
    dateStart: String(work.date.start),
    dateEnd: work.date.end !== undefined ? String(work.date.end) : "",
    ongoing: Boolean(work.date.ongoing),
    concept: pair(work.concept),
    medium: pair(work.medium),
    dimensions: pair(work.dimensions),
    durationSeconds: work.durationSeconds !== undefined ? String(work.durationSeconds) : "",
    media: mediaToForm(work.media),
    installations: [...work.installations]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((i) => ({
        key: rowKey(),
        id: i.id,
        exhibitionId: i.exhibitionId ?? "",
        title: pair(i.title),
        year: i.year !== undefined ? String(i.year) : "",
        venueOverride: pair(i.venueOverride),
        description: pair(i.description),
        dimensions: pair(i.dimensions),
        media: mediaToForm(i.media),
        technicalInternal: i.technicalInternal ?? "",
      })),
    credits: work.credits.map((c) => ({ key: rowKey(), role: pair(c.role), name: pair(c.name) })),
    technicalPublic: pair(work.technical?.public),
    technicalInternal: work.technical?.internal ?? "",
    version: work.version,
  };
}

/** Request body for POST (version ignored) and PUT /works. */
export function workFromForm(form: WorkForm): WorkUpdate {
  const technical = compact({ public: optional(form.technicalPublic), internal: optionalString(form.technicalInternal) });
  const installations: Installation[] = form.installations.map((i, index) =>
    compact({
      id: i.id,
      exhibitionId: optionalString(i.exhibitionId),
      title: optional(i.title),
      year: optionalNumber(i.year),
      venueOverride: optional(i.venueOverride),
      description: optional(i.description),
      dimensions: optional(i.dimensions),
      media: mediaFromForm(i.media),
      technicalInternal: optionalString(i.technicalInternal),
      displayOrder: index,
    }),
  );
  const credits: Credit[] = form.credits.map((c) => ({ role: required(c.role), name: required(c.name) }));

  return compact({
    slug: form.slug.trim(),
    title: required(form.title),
    date: compact({
      start: Number(form.dateStart),
      end: form.ongoing ? undefined : optionalNumber(form.dateEnd),
      ongoing: form.ongoing || undefined,
    }),
    type: form.type,
    // Markdown is stored as written; only surrounding whitespace is trimmed.
    concept: required(form.concept),
    medium: optional(form.medium),
    dimensions: optional(form.dimensions),
    durationSeconds: optionalNumber(form.durationSeconds),
    media: mediaFromForm(form.media),
    installations,
    credits,
    technical: Object.keys(technical).length ? technical : undefined,
    version: form.version,
  });
}

// ---------------------------------------------------------------------------
// Exhibition
// ---------------------------------------------------------------------------

export function emptyExhibitionForm(): ExhibitionForm {
  return { slug: "", title: emptyPair(), type: "solo", venue: "", city: "", country: "", startDate: "", endDate: "", url: "" };
}

export function exhibitionToForm(e: ExhibitionDocument): ExhibitionForm {
  return {
    slug: e.slug ?? "",
    title: pair(e.title),
    type: e.type,
    venue: e.venue ?? "",
    city: e.city ?? "",
    country: e.country ?? "",
    startDate: e.startDate ?? "",
    endDate: e.endDate ?? "",
    url: e.url ?? "",
  };
}

export function exhibitionFromForm(form: ExhibitionForm): ExhibitionInput {
  return compact({
    slug: optionalString(form.slug),
    title: required(form.title),
    type: form.type,
    venue: optionalString(form.venue),
    city: optionalString(form.city),
    country: optionalString(form.country),
    startDate: optionalString(form.startDate),
    endDate: optionalString(form.endDate),
    url: optionalString(form.url),
  });
}

/** Suggests a slug from an English title. Never applied automatically after creation. */
export function suggestSlug(title: string): string {
  return title
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}
